import React from 'react';
import { cn, isLollySkin } from '@utils/index';

interface PlanHowItWorksProps {
  deductible?: number;
  outOfPocketMax?: number;
  planPaysPercent?: number;
  userPaysPercent?: number;
}

export const PlanHowItWorks: React.FC<PlanHowItWorksProps> = ({
  deductible = 1500,
  outOfPocketMax = 12000,
  planPaysPercent = 80,
  userPaysPercent = 20,
}) => {
  const formatPercent = (value: number) =>
    Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
  const planPaysLabel = formatPercent(planPaysPercent);
  const userPaysLabel = formatPercent(userPaysPercent);
  const planShareWidth = `${planPaysPercent}%`;
  const userShareWidth = `${userPaysPercent}%`;

  return (
    <div className="mt-8">
      <h2 className="text-[2vw] font-bold mb-8 tracking-wide max-lg:hidden">
        Here's how your plan works
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-3 max-lg:hidden">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className={cn('px-6 py-3 rounded-t-3xl', isLollySkin() ? 'bg-lolly-profile-bg' : 'bg-profile-header-bg')}>
            <div className="flex items-center gap-3">
              <span className="text-white text-[82px] max-1xl:text-[60px] leading-none font-bold">
                1
              </span>
              <p className="text-white max-1xl:text-lg text-2xl font-medium">
                You pay for care until you hit your deductible.
              </p>
            </div>
          </div>
          <div className="p-6 max-1xl:p-3 text-center">
            <p className="text-2xl max-1xl:text-xl mt-2 mb-5">
              You are responsible for all costs associated with your care until
              you've met your deductible amount.
            </p>
            <div className="mb-2">
              <p className="text-primary text-2xl max-1xl:text-xl font-medium">
                This is your deductible:
              </p>
              <p className="text-primary text-[4vw] font-bold">
                ${deductible.toLocaleString()}
              </p>
            </div>
            {/* <p className="text-gray-400 text-xs mt-4">
              *Your deductible is waived for certain services.
            </p> */}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className={cn('px-6 py-3 rounded-t-3xl', isLollySkin() ? 'bg-lolly-profile-bg' : 'bg-profile-header-bg')}>
            <div className="flex items-center gap-3">
              <span className="text-white text-[82px] max-1xl:text-[60px] leading-none font-bold">
                2
              </span>
              <p className="text-white text-2xl max-1xl:text-lg font-medium">
                At this point, your plan pays for all or some of your care
                costs.*
              </p>
            </div>
          </div>
          <div className="p-6 max-1xl:p-3 text-center">
            <p className="text-2xl max-1xl:text-xl mt-5 mb-5">
              Once you've met your deductible, the plan pays{' '}
              <strong>{planPaysLabel}</strong> of covered care costs and you
              pay <strong>{userPaysLabel}</strong>.
            </p>
            <div className="flex rounded-lg m-auto max-w-[85%] overflow-hidden">
              <div
                className="bg-[#ebdae2] px-4 py-3 flex min-w-0 flex-col justify-center"
                style={{ width: planShareWidth }}
              >
                <p className="text-primary text-xl font-bold">The Plan</p>
                <p className="text-primary text-xl font-bold">
                  {planPaysLabel}
                </p>
              </div>
              {userPaysPercent > 0 && (
                <div
                  className="bg-primary px-1.5 py-3 flex min-w-0 flex-col items-center justify-center"
                  style={{ width: userShareWidth }}
                >
                  <p className="text-white text-lg font-bold leading-tight">
                    You
                  </p>
                  <p className="text-white text-lg font-bold leading-tight">
                    {userPaysLabel}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className={cn('px-6 py-3 rounded-t-3xl', isLollySkin() ? 'bg-lolly-profile-bg' : 'bg-profile-header-bg')}>
            <div className="flex items-center gap-3">
              <span className="text-white text-[82px] max-1xl:text-[60px] leading-none font-bold">
                3
              </span>
              <p className="text-white text-2xl max-1xl:text-lg font-medium">
                Once you hit your out-of-pocket maximum, you pay only copays.*
              </p>
            </div>
          </div>
          <div className="p-6 max-1xl:p-3 text-center">
            <p className="text-gray-700 text-2xl max-1xl:text-xl mb-2">
              When you or your family reach your plan's out-of-pocket maximum,
              the plan pays <strong>100%</strong> of all covered care costs.
            </p>
            <div>
              <p className="text-2xl font-medium max-1xl:text-xl">
                Your Out-of-Pocket Max is:
              </p>
              <p className="text-primary text-[4vw] font-bold">
                ${outOfPocketMax.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
      <p className="pb-10 text-sm text-gray-500 max-lg:hidden">
        *This is a simplified representation. Refer to your Summary Plan
        Description for full details.
      </p>
    </div>
  );
};
